import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Footprints } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import Prose from "@/components/Prose";
import WeatherWidget from "@/components/WeatherWidget";
import ShareButton, { usePageMeta } from "@/components/Share";
import NotFound from "@/pages/NotFound";

export default function LocationDetail() {
  const { slug } = useParams();
  const [l, setL] = useState(undefined);
  useEffect(() => {
    api.get(`/public/locations/${slug}`).then(({ data }) => setL(data)).catch(() => setL(null));
  }, [slug]);
  usePageMeta(l?.title, l?.summary);
  const at = useMemo(() => (l?.lat != null && l?.lon != null ? { lat: l.lat, lon: l.lon, label: l.title } : null), [l]);

  if (l === undefined) return <p className="max-w-7xl mx-auto px-8 pt-20 text-sm text-[#5a665e]">Loading…</p>;
  if (l === null) return <NotFound />;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14" data-testid="location-detail-page">
      <div className="flex items-center justify-between gap-4">
        <Link to="/locations" className="inline-flex items-center gap-2 text-sm text-[#5a665e] hover:text-[#1f402b]" data-testid="location-back-link"><ArrowLeft size={14} /> All locations</Link>
        <ShareButton kind="locations" slug={l.slug} title={l.title} />
      </div>
      <div className="mt-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <p className="eyebrow">{[l.region, l.status].filter(Boolean).join(" · ")}</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1a261f]" data-testid="location-title">{l.title}</h1>
          <p className="mt-5 text-lg text-[#5a665e] leading-relaxed font-display italic">{l.summary}</p>
          {l.cover_image && <img src={imgUrl(l.cover_image)} alt={l.title} className="mt-10 w-full aspect-[16/9] object-cover" />}
          <div className="mt-12 max-w-2xl"><Prose text={l.body || ""} /></div>
        </div>
        <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-24 self-start order-first lg:order-none">
          {at ? <WeatherWidget at={at} /> : <p className="text-sm text-[#5a665e]">No coordinates set for live weather.</p>}
          {l.access && (
            <div className="border border-[#e2dcd4] bg-white p-6" data-testid="location-access">
              <p className="eyebrow flex items-center gap-2"><Footprints size={13} /> Access</p>
              <p className="mt-3 text-sm text-[#3c4a41] leading-relaxed">{l.access}</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
